import React from "react";
import { useField } from "react-final-form";
import { Modal, TextArea, TextAreaBase } from "@truevault/aquilo";
import SegmentedTabs from "@truevault/aquilo/src/components/segmentedTabs/SegmentedTabs";
import { ConsentLinkButton, ConsentSettingRow } from "../../../consentBanner/ConsentSettingRow";
import {
  CONSENT_READONLY_TEXTAREA_CLASSES,
  CONSENT_TEXTAREA_CLASSES,
} from "../../../consentBanner/consentFieldStyles";

interface ConsentBannerCopyOverrideRowProps {
  formApi: {
    change: (field: string, value: string) => void;
  };
  // Which banner this text is for, e.g. "opt-in". Used in the confirmation copy.
  bannerName: string;
  title?: string;
  description?: string;
  field: string;
  resetText?: string;
  startToggled?: boolean;
}

type TextMode = "default" | "custom";

/**
 * Default or custom banner text. Switching either way asks for confirmation first, since custom text means owning the
 * on-page disclosure and reverting discards it.
 */
export const ConsentBannerCopyOverrideRow = ({
  formApi,
  bannerName,
  title = "Banner text",
  description,
  field,
  resetText = "",
  startToggled = false,
}: ConsentBannerCopyOverrideRowProps) => {
  // Can't read from the form state yet to see if we start toggled, parent needs to determine this.
  const [mode, setMode] = React.useState<TextMode>(startToggled ? "custom" : "default");
  const [pendingMode, setPendingMode] = React.useState<TextMode | null>(null);

  // Keep the field registered while the default text is showing, so react-final-form still counts it toward
  // `dirty` and submits it.
  useField(field, { subscription: {} });

  const applyMode = (nextMode: TextMode) => {
    setMode(nextMode);
    setPendingMode(null);
    formApi.change(field, nextMode === "custom" ? resetText : "");
  };

  return (
    <>
      <ConsentSettingRow title={title} helpText={description}>
        <SegmentedTabs<TextMode>
          options={[
            { label: "Default", value: "default" },
            { label: "Custom", value: "custom" },
          ]}
          selected={mode}
          onChange={(nextMode) => {
            if (nextMode !== mode) {
              setPendingMode(nextMode);
            }
          }}
        />
        <div className="w-full">
          {mode === "custom" ? (
            <TextArea
              field={field}
              className={CONSENT_TEXTAREA_CLASSES}
              aria-label={`Custom ${bannerName} banner text`}
              rows={5}
              required
            />
          ) : (
            <TextAreaBase
              field={`${field}-default`}
              aria-label={`Default ${bannerName} banner text`}
              value={resetText}
              rows={5}
              readOnly
              className={CONSENT_READONLY_TEXTAREA_CLASSES}
            />
          )}
        </div>
        {mode === "custom" && (
          <ConsentLinkButton onClick={() => setPendingMode("default")}>
            Reset to default
          </ConsentLinkButton>
        )}
      </ConsentSettingRow>
      {pendingMode === "default" && (
        <Modal
          title="Banner text will revert to default"
          showOverlay
          closeOnOverlayClick={false}
          closeButton={true}
          onClose={() => setPendingMode(null)}
          firstButton="Keep custom text"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setPendingMode(null)}
          secondButton="Revert to default"
          secondButtonColor="primary"
          onSecondButtonClick={() => applyMode("default")}
          alignButton="right"
          alignText="left"
        >
          This will change the banner text back to the default TrueVault banner text and your
          changes will not be saved.
        </Modal>
      )}
      {pendingMode === "custom" && (
        <Modal
          title={`Customize the ${bannerName} banner text?`}
          showOverlay
          closeOnOverlayClick={false}
          closeButton={true}
          onClose={() => setPendingMode(null)}
          firstButton="Keep default text"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setPendingMode(null)}
          secondButton="Customize text"
          secondButtonColor="primary"
          onSecondButtonClick={() => applyMode("custom")}
          alignButton="right"
          alignText="left"
        >
          By using custom text for the opt-in or opt-out banner, you're taking ownership of your
          on-page disclosures. We believe our standard text should serve most businesses, but if you
          have specific needs, you may proceed to customize your banner text.
        </Modal>
      )}
    </>
  );
};
